import React, { useEffect, useRef, useState } from 'react';
import { useUnsavedChangesSource } from './appUpdate';
import { Box, Typography, Button, Drawer, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { integrationApiService, IntegrationConfig, IntegrationSettings } from '../services/integrationApi';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { INTEGRATION_SETTINGS_COPY as COPY } from '../utils/integrationSettingsCopy';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useOrgFeatureFlags } from '../hooks/useOrgFeatureFlags';
import ProPublicSyncSettingsSection from './ProPublicSyncSettingsSection';
import ShopifySetupChecklist from './ShopifySetupChecklist';
import ShopifyPricingZonesSection from './ShopifyPricingZonesSection';
import ShopifyRebateDiscountsSection from './ShopifyRebateDiscountsSection';
import RebatesOnlySection from './RebatesOnlySection';
import WooCommerceCompareSection from './WooCommerceCompareSection';
import ShopifyFieldNamespaceRow from './ShopifyFieldNamespaceRow';
import ShopifyListingShapeSection from './ShopifyListingShapeSection';
import ShopifyStoreDeletesSection from './ShopifyStoreDeletesSection';
import StoreSpecsPushSection from './StoreSpecsPushSection';
import RelatedProductsSettingsSection from './RelatedProductsSettingsSection';
import ShopifyDescriptionTemplateSection from './ShopifyDescriptionTemplateSection';
import ShopifyVariantConsolidationSection from './ShopifyVariantConsolidationSection';
import {
  DataSyncSection,
  SyncModeSection,
  TagsAutomationSection,
  normalizeIntegrationSettings,
  useShopifyScopeControls,
} from './IntegrationSettingsSections';
import { rebateDiscountOn, runRebateDiscountSyncs } from './integrationSetup/rebateDiscountSync';
import { lightspeedCatalogModes } from './integrationSetup/walkthroughSteps';
import { LIGHTSPEED_SETTINGS_SECTIONS, isLightspeedSectionShown } from './lightspeed/lightspeedSettingsRegistry';
import LightspeedEcomSettingsSection from './lightspeedEcom/LightspeedEcomSettingsSection';
import LightspeedEcomScanCard from './lightspeedEcom/LightspeedEcomScanCard';

export interface IntegrationSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  integration: IntegrationConfig | null;
  /** `page` renders the same editor in the detail Sync Behavior tab instead of a drawer. */
  variant?: 'drawer' | 'page';
  /** Set (flag `integration_setup_walkthrough`, walkthrough finished) to offer "Run setup again". */
  onRunSetupAgain?: () => void;
}

/**
 * Edit sync-related settings without leaving the integration detail / sync history view.
 */
const IntegrationSettingsDrawer: React.FC<IntegrationSettingsDrawerProps> = ({
  open,
  onClose,
  integration,
  variant = 'drawer',
  onRunSetupAgain,
}) => {
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState<IntegrationSettings | null>(null);
  const [orderSyncEnabled, setOrderSyncEnabled] = useState(false);
  const scope = useShopifyScopeControls(integration);
  const { isEnabled: isOrgFlagEnabled } = useOrgFeatureFlags();
  const { t } = useAppTranslation();

  // The register-discount switch is one of the registry's sections; saving it also syncs the Discounts.
  const lightspeedDiscountsShown =
    integration?.platform === 'lightspeed' &&
    LIGHTSPEED_SETTINGS_SECTIONS.some((s) => s.id === 'rebate-discounts' && isLightspeedSectionShown(s, isOrgFlagEnabled));

  const active = variant === 'page' || open;
  // The page variant stays mounted while the integration refetches (Sync Now,
  // window focus), so only re-seed the form when nothing is unsaved or a
  // different integration is shown.
  const dirtyRef = useRef(false);
  const seededIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!active) {
      dirtyRef.current = false;
      seededIdRef.current = null;
      return;
    }
    if (!integration) return;
    if (dirtyRef.current && seededIdRef.current === integration.id) return;
    setSettings(normalizeIntegrationSettings(integration.settings));
    setOrderSyncEnabled(integration.orderSyncEnabled === true);
    seededIdRef.current = integration.id;
    dirtyRef.current = false;
  }, [active, integration]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!integration || !settings) throw new Error('No integration');

      // The Mapping tab owns these; leaving them out keeps this save from
      // writing back a copy seeded before the mapping was last saved.
      const {
        storeLayout: _storeLayout,
        specMetafields: _specMetafields,
        lightspeedLayout: _lightspeedLayout,
        lightspeedCustomFields: _lightspeedCustomFields,
        ...ownSettings
      } = {
        ...integration.settings,
        ...settings,
      };
      const results = await Promise.all([
        integrationApiService.updateIntegration(integration.id, {
          settings: ownSettings as IntegrationSettings,
        }),
        orderSyncEnabled !== (integration.orderSyncEnabled === true)
          ? (async () => {
              const headers = await getAuthHeaders();
              return makeApiCall(`/integrations/${integration.id}/order-sync`, {
                method: 'PATCH',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ orderSyncEnabled }),
              });
            })()
          : Promise.resolve(null),
      ]);

      // A changed rebate-discount switch is applied now, not at the next scheduled
      // sync, so turning one off takes its discounts down at once. Best effort:
      // the settings are saved either way and the section's Sync now retries.
      if (integration.platform === 'shopify') {
        await runRebateDiscountSyncs(integration.id, integration.settings.rebateDiscounts, settings.rebateDiscounts, {
          instant: isOrgFlagEnabled('shopify_rebate_discounts'),
          bundle: isOrgFlagEnabled('bundle_shopify_discounts'),
        });
      }
      if (
        integration.platform === 'lightspeed' &&
        lightspeedDiscountsShown &&
        rebateDiscountOn(integration.settings.rebateDiscounts, 'instant') !== rebateDiscountOn(settings.rebateDiscounts, 'instant')
      ) {
        await integrationApiService.syncLightspeedRebateDiscounts(integration.id).catch(() => null);
      }

      return results[0];
    },
    onSuccess: () => {
      dirtyRef.current = false;
      queryClient.invalidateQueries({ queryKey: ['integration', integration?.id] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      queryClient.invalidateQueries({ queryKey: ['integrationHistory', integration?.id] });
      if (variant === 'drawer') onClose();
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to save settings';
      window.alert(msg);
    },
  });

  // Lets the app-update prompt wait for, save or drop these edits. `dirtyRef`
  // is only written by user edits (which re-render) and by save/discard.
  useUnsavedChangesSource({
    dirty: active && dirtyRef.current,
    save: () => saveMutation.mutateAsync(),
    discard: () => {
      if (!integration) return;
      setSettings(normalizeIntegrationSettings(integration.settings));
      setOrderSyncEnabled(integration.orderSyncEnabled === true);
      dirtyRef.current = false;
    },
  });

  if (!integration || !settings) return null;

  const isShopify = integration.platform === 'shopify';
  const lightspeedModes =
    integration.platform === 'lightspeed'
      ? lightspeedCatalogModes({
          lightspeed_link_only: isOrgFlagEnabled('lightspeed_link_only'),
          lightspeed_catalog_parity: isOrgFlagEnabled('lightspeed_catalog_parity'),
        })
      : null;
  const set = (partial: Partial<IntegrationSettings>) => {
    dirtyRef.current = true;
    setSettings((s) => (s ? { ...s, ...partial } : s));
  };

  const frame = (children: React.ReactNode) =>
    variant === 'page' ? (
      <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: '14px' }}>
        {children}
      </Box>
    ) : (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 440 },
            maxWidth: '100vw',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {children}
      </Drawer>
    );

  return frame(
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {variant === 'drawer' && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Integration settings
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Close />
          </IconButton>
        </Box>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            p: 2,
            pb: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {onRunSetupAgain && (
            <Button size="small" variant="outlined" onClick={onRunSetupAgain} sx={{ alignSelf: 'flex-start' }}>
              {t('integrations.setupWalkthrough.runAgain')}
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55, display: 'block' }}>
            {COPY.intro}
            {isShopify ? ' Product type and category live on the Classification tab, spec metafields on the Metafields tab.' : ''}
          </Typography>

          {/* Lightspeed eCom (flag lightspeed_ecom_connector) is content-only: its own switches replace the data-sync ones. */}
          {integration.platform === 'lightspeed_ecom' ? (
            <>
              <LightspeedEcomSettingsSection settings={settings} onChange={set} />
              <LightspeedEcomScanCard integration={integration} />
            </>
          ) : (
            <DataSyncSection
              settings={settings}
              onChange={set}
              platform={integration.platform}
              scope={scope}
            />
          )}

          <ProPublicSyncSettingsSection
            platform={integration.platform}
            allowProPublicSync={settings.allowProPublicSync === true}
            allowProPublicSyncAcknowledgedAt={settings.allowProPublicSyncAcknowledgedAt}
            allowProPublicSyncAcknowledgedBy={settings.allowProPublicSyncAcknowledgedBy}
            onAllowProChange={(v) => set({ allowProPublicSync: v })}
          />

          <TagsAutomationSection
            settings={settings}
            onChange={set}
            platform={integration.platform}
            scope={scope}
            syncTimeZone={integration.syncTimeZone}
            lastSyncSlotAt={integration.syncSchedule?.lastSyncSlotAt}
            orderSync={{
              enabled: orderSyncEnabled,
              onChange: (value: boolean) => {
                dirtyRef.current = true;
                setOrderSyncEnabled(value);
              },
            }}
          />

          {isShopify && (
            <ShopifySetupChecklist
              shopId={integration.credentials?.shopId ? String(integration.credentials.shopId) : null}
              integrationId={integration.id}
            />
          )}

          {isShopify && <ShopifyPricingZonesSection integrationId={integration.id} />}

          {isShopify && (
            <ShopifyRebateDiscountsSection
              integration={integration}
              rebateDiscounts={settings.rebateDiscounts}
              onRebateDiscountsChange={(rebateDiscounts) => set({ rebateDiscounts })}
            />
          )}

          {/* Lightspeed Retail mounts it through its section registry; every other platform shows why it is unavailable. */}
          {integration.platform !== 'lightspeed' && <RebatesOnlySection integration={integration} />}

          {isShopify && <ShopifyFieldNamespaceRow integrationId={integration.id} />}

          {isShopify && <SyncModeSection settings={settings} onChange={set} />}

          <StoreSpecsPushSection settings={settings} onChange={set} platform={integration.platform} />

          <RelatedProductsSettingsSection settings={settings} onChange={set} platform={integration.platform} integrationId={integration.id} />

          {integration.platform === 'woocommerce' && (
            <WooCommerceCompareSection
              integrationId={integration.id}
              compareRows={settings.compareRows === true}
              onChange={(compareRows) => set({ compareRows })}
            />
          )}
          {isShopify && (
            <ShopifyListingShapeSection settings={settings} onChange={set} shopifyPlus={integration.isShopifyPlus ?? null} />
          )}
          {isShopify && <ShopifyStoreDeletesSection settings={settings} onChange={set} />}
          {isShopify && <ShopifyDescriptionTemplateSection settings={settings} onChange={set} integrationId={integration.id} />}
          {isShopify && <ShopifyVariantConsolidationSection integrationId={integration.id} />}
          {integration.platform === 'lightspeed' && lightspeedModes && (
            <SyncModeSection
              settings={settings}
              onChange={set}
              modes={lightspeedModes}
              copyVariant="lightspeed"
              linkOnlyPricingDefaultsOn={isOrgFlagEnabled('lightspeed_catalog_parity')}
            />
          )}
          {integration.platform === 'lightspeed' &&
            LIGHTSPEED_SETTINGS_SECTIONS.map((section) =>
              isLightspeedSectionShown(section, isOrgFlagEnabled) ? (
                <section.Component key={section.id} integration={integration} settings={settings} onChange={set} />
              ) : null,
            )}
        </Box>

        <Box
          sx={{
            p: 2,
            flexShrink: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 1,
            justifyContent: 'flex-end',
          }}
        >
          {variant === 'drawer' && (
            <Button onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save settings'}
          </Button>
        </Box>
      </Box>
  );
};

export default IntegrationSettingsDrawer;
